/**
 * ConnectionStatus — WebSocket connection health indicator.
 *
 * Reads the `connectionStatus` Redux slice and renders a colour-coded
 * dot + label:
 * - 🟢 `connected`    — data flowing
 * - 🟡 `connecting` / `reconnecting` — handshake or auto-retry in progress
 * - 🔴 `disconnected` — no STOMP session
 *
 * Also displays a tooltip with the elapsed time since the last tick.
 */
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import type { ConnectionState } from "@store/connection-status-slice";
import "./ConnectionStatus.scss";

const STATUS_META: Record<ConnectionState, { label: string; className: string }> = {
  connected: { label: "Connected", className: "status--connected" },
  connecting: { label: "Connecting…", className: "status--connecting" },
  reconnecting: { label: "Reconnecting…", className: "status--reconnecting" },
  disconnected: { label: "Disconnected", className: "status--disconnected" },
};

const ConnectionStatus: React.FC = () => {
  const status: ConnectionState = useSelector(
    (state: any) => state.connectionStatus?.status ?? "disconnected"
  );
  const lastDataAt: string | null = useSelector(
    (state: any) => state.connectionStatus?.lastDataAt ?? null
  );
  const reconnectCount: number = useSelector(
    (state: any) => state.connectionStatus?.reconnectCount ?? 0
  );

  const [elapsed, setElapsed] = useState("");

  // Refresh elapsed time every second
  useEffect(() => {
    if (!lastDataAt) {
      setElapsed("");
      return;
    }
    const tick = () => {
      const diff = Math.floor((Date.now() - new Date(lastDataAt).getTime()) / 1000);
      if (diff < 60) setElapsed(`${diff}s ago`);
      else if (diff < 3600) setElapsed(`${Math.floor(diff / 60)}m ago`);
      else setElapsed(`${Math.floor(diff / 3600)}h ago`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lastDataAt]);

  const meta = STATUS_META[status] || STATUS_META.disconnected;

  const tooltip = [
    `WebSocket: ${meta.label}`,
    elapsed ? `Last data: ${elapsed}` : null,
    reconnectCount > 0 ? `Reconnects: ${reconnectCount}` : null,
  ]
    .filter(Boolean)
    .join("\n");

  return (
    <div className={`connection-status ${meta.className}`}>
      <span className="connection-dot" />
      <span className="connection-label">{meta.label}</span>
    </div>
  );
};

export default ConnectionStatus;
